import React, { useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line,
  LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { getAdminAnalytics } from "./adminApi";
import "./AnalyticsOverview.css";

const COLORS = ["#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4", "#64748b"];
const number = value => Number(value || 0);
const money = (value, currency) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: currency || "INR", maximumFractionDigits: 0,
    }).format(number(value));
  } catch {
    return `${currency || "INR"} ${number(value).toLocaleString("en-IN")}`;
  }
};
const compactMoney = (value, currency) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: currency || "INR", notation: "compact", maximumFractionDigits: 1,
    }).format(number(value));
  } catch {
    return number(value).toLocaleString("en-IN");
  }
};
const shorten = (value, max = 20) => String(value || "Unknown").length > max
  ? `${String(value).slice(0, max - 1)}…` : String(value || "Unknown");

function ChartPanel({ title, subtitle, children, wide = false }) {
  return (
    <section className={`ra-chart-panel${wide ? " ra-chart-wide" : ""}`}>
      <div className="ra-chart-heading"><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>
      {children}
    </section>
  );
}

function EmptyChart({ children }) {
  return <div className="ra-chart-empty">{children}</div>;
}

export default function AnalyticsOverview({ restaurantId }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setData(null);
    getAdminAnalytics(restaurantId, days)
      .then(result => { if (active) setData(result); })
      .catch(err => { if (active) setError(err.message || "Could not load analytics."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [restaurantId, days]);

  const currency = data?.currency || "INR";
  const summary = data?.summary;
  const sales = useMemo(() => (data?.sales_trend || []).map(row => ({
    ...row, label: String(row.date || "").slice(5), sales: number(row.sales), orders: number(row.orders),
  })), [data]);
  const items = useMemo(() => (data?.top_items || []).slice(0, 8).map(row => ({
    ...row, label: shorten(row.item_name), quantity: number(row.quantity), revenue: number(row.revenue),
  })), [data]);
  const payments = useMemo(() => (data?.payment_methods || [])
    .map(row => ({ name: row.payment_method || "Other", value: number(row.amount) }))
    .filter(row => row.value > 0), [data]);
  const statuses = useMemo(() => (data?.order_statuses || [])
    .map(row => ({ name: row.status || "Unknown", value: number(row.count) }))
    .filter(row => row.value > 0), [data]);
  const hours = useMemo(() => (data?.peak_hours || []).map(row => ({
    label: `${String(row.hour).padStart(2, "0")}:00`, orders: number(row.orders),
  })), [data]);

  // Optional additions to the EXISTING /admin/analytics/ response.
  // No second API endpoint or extra frontend component is needed.
  const categorySales = useMemo(() => (data?.category_sales || []).map(row => ({
    name: row.category_name || row.category || row.name || "Uncategorized",
    value: number(row.revenue ?? row.sales ?? row.value),
  })).filter(row => row.value > 0).sort((a, b) => b.value - a.value), [data]);
  const lowItems = useMemo(() => (data?.low_selling_items || []).slice(0, 8).map(row => ({
    label: shorten(row.item_name || row.name), quantity: number(row.quantity),
  })), [data]);
  const weeklyComparison = useMemo(() => (data?.weekly_comparison || []).map(row => ({
    day: row.day || row.label || "",
    current: number(row.current ?? row.this_week),
    previous: number(row.previous ?? row.last_week),
  })), [data]);

  const itemComparison = (data?.selling_items_comparison || []).map(row => ({
    label: shorten(row.item_name), thisWeek: number(row.this_week), lastWeek: number(row.last_week),
  }));
  const cards = summary ? [
    ["Paid sales", money(summary.revenue, currency), "wallet"],
    ["Paid orders", number(summary.paid_orders).toLocaleString("en-IN"), "receipt"],
    ["All orders", number(summary.total_orders).toLocaleString("en-IN"), "orders"],
    ["Items sold", number(summary.items_sold).toLocaleString("en-IN"), "items"],
    ["Average paid order", money(summary.average_order_value, currency), "average"],
  ] : [];

  const chartHeight = 290;
  const paidShare = summary && number(summary.total_orders) > 0
    ? Math.round(number(summary.paid_orders) / number(summary.total_orders) * 100)
    : null;
  const topItem = items.length ? items[0] : null;
  const busiestHour = hours.reduce((best, row) => (!best || row.orders > best.orders ? row : best), null);

  return (
    <div className="ra-analytics">
      <div className="ra-analytics-top">
        <div><h2>Sales & performance</h2><p>Revenue, popular dishes and ordering activity for your restaurant</p></div>
        <div className="ra-period" aria-label="Reporting period">
          {[1, 7, 30].map(n => (
            <button key={n} type="button" aria-pressed={days === n}
              className={days === n ? "selected" : ""} onClick={() => setDays(n)}>
              {n === 1 ? "Today" : `${n} days`}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="ra-analytics-message" role="status">Loading analytics…</p>}
      {error && <p className="ra-analytics-message ra-error" role="alert">{error}</p>}
      {!loading && !error && data && (
        <>
          <p className="ra-analytics-dates">{data.start_date} to {data.end_date} · Paid revenue excludes cancelled orders.</p>
          <div className="ra-analytics-kpis">
            {cards.map(([label, value, key]) => (
              <article key={key}><span>{label}</span><strong>{value}</strong></article>
            ))}
          </div>

          <div className="ra-analytics-insights">
            <div><span>Paid order share</span><strong>{paidShare === null ? "—" : `${paidShare}%`}</strong><small>Paid orders / all orders</small></div>
            <div><span>Top-selling dish</span><strong>{topItem ? topItem.item_name : "—"}</strong><small>{topItem ? `${topItem.quantity} items sold` : "No paid items yet"}</small></div>
            <div><span>Busiest ordering hour</span><strong>{busiestHour && busiestHour.orders ? busiestHour.label : "—"}</strong><small>{busiestHour && busiestHour.orders ? `${busiestHour.orders} orders` : "No orders yet"}</small></div>
          </div>

          <div className="ra-analytics-grid">
            <ChartPanel title="Daily sales trend" subtitle="Revenue from successfully paid orders" wide>
              <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <LineChart data={sales} margin={{ top: 8, right: 20, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis width={65} tickFormatter={v => compactMoney(v, currency)} />
                  <Tooltip formatter={v => money(v, currency)} />
                  <Line type="monotone" dataKey="sales" stroke="#2563eb" strokeWidth={3} dot={{ r: 3 }} name="Sales" />
                </LineChart>
              </ResponsiveContainer></div>
            </ChartPanel>

            <ChartPanel title="Best-selling menu items" subtitle="Top dishes ranked by quantity sold" wide>
              {items.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={items} layout="vertical" margin={{ left: 12, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="label" type="category" width={135} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="quantity" fill="#10b981" radius={[0, 5, 5, 0]} name="Quantity sold" />
                </BarChart>
              </ResponsiveContainer></div> : <EmptyChart>No paid items for this period.</EmptyChart>}
            </ChartPanel>

            <ChartPanel title="Payment method mix" subtitle="Share of successful payment amounts">
              {payments.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart><Pie data={payments} dataKey="value" nameKey="name" innerRadius={53} outerRadius={89} paddingAngle={3}>
                  {payments.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip formatter={v => money(v, currency)} /><Legend /></PieChart>
              </ResponsiveContainer></div> : <EmptyChart>No successful payments for this period.</EmptyChart>}
            </ChartPanel>

            <ChartPanel title="Order status distribution" subtitle="All orders, including unpaid and cancelled">
              {statuses.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart><Pie data={statuses} dataKey="value" nameKey="name" outerRadius={90}>
                  {statuses.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip /><Legend /></PieChart>
              </ResponsiveContainer></div> : <EmptyChart>No orders for this period.</EmptyChart>}
            </ChartPanel>

            <ChartPanel title="Orders by hour" subtitle="When customers place orders" wide>
              <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={hours}><CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" interval={3} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} /><Tooltip />
                  <Bar dataKey="orders" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Orders" />
                </BarChart>
              </ResponsiveContainer></div>
            </ChartPanel>

            <ChartPanel title="Revenue by best-selling dish" subtitle="Revenue contribution of the top items" wide>
              {items.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={items} layout="vertical" margin={{ left: 12, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={v => compactMoney(v, currency)} />
                  <YAxis dataKey="label" type="category" width={135} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => money(v, currency)} />
                  <Bar dataKey="revenue" fill="#f59e0b" radius={[0, 5, 5, 0]} name="Item revenue" />
                </BarChart>
              </ResponsiveContainer></div> : <EmptyChart>No paid items for this period.</EmptyChart>}
            </ChartPanel>

            <ChartPanel title="Orders and sales together" subtitle="Compare daily order volume with paid revenue" wide>
              <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <ComposedChart data={sales} margin={{ top: 8, right: 10, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="orders" allowDecimals={false} width={38} />
                  <YAxis yAxisId="sales" orientation="right" width={65} tickFormatter={v => compactMoney(v, currency)} />
                  <Tooltip formatter={(v, name) => name === "Paid sales" ? money(v, currency) : v} />
                  <Legend />
                  <Bar yAxisId="orders" dataKey="orders" name="Paid orders" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="sales" dataKey="sales" name="Paid sales" type="monotone" stroke="#f43f5e" strokeWidth={2.5} />
                </ComposedChart>
              </ResponsiveContainer></div>
            </ChartPanel>

            <ChartPanel title="Sales by food category" subtitle="Revenue split across menu categories">
              {categorySales.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart><Pie data={categorySales} dataKey="value" nameKey="name" innerRadius={53} outerRadius={89}>
                  {categorySales.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip formatter={v => money(v, currency)} /><Legend /></PieChart>
              </ResponsiveContainer><div className="ra-category-leader">Top category: <strong>{categorySales[0].name}</strong> · {money(categorySales[0].value, currency)} · {(100 * categorySales[0].value / categorySales.reduce((sum, row) => sum + row.value, 0)).toFixed(1)}% of category sales</div></div> : <EmptyChart>No paid category sales for this period.</EmptyChart>}
            </ChartPanel>

            <ChartPanel title="Low-selling items" subtitle="Least sold items, including zero-sale menu items">
              {lowItems.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={lowItems} layout="vertical" margin={{ left: 12, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="label" type="category" width={135} tick={{ fontSize: 11 }} />
                  <Tooltip /><Bar dataKey="quantity" fill="#f43f5e" radius={[0, 5, 5, 0]} name="Quantity sold" />
                </BarChart>
              </ResponsiveContainer></div> : <EmptyChart>Low-selling item data is not available from the current API.</EmptyChart>}
            </ChartPanel>

            <ChartPanel title="Items sold: this week vs last week" subtitle="Quantity sold by dish (matching weekdays)" wide>
              {itemComparison.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={itemComparison} margin={{ top: 8, right: 16, bottom: 50, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" angle={-25} textAnchor="end" interval={0} height={70} tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} /><Tooltip /><Legend />
                  <Bar dataKey="thisWeek" name="This week" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lastWeek" name="Last week" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer></div> : <EmptyChart>No paid item sales in these weeks.</EmptyChart>}
            </ChartPanel>
            <ChartPanel title="This week vs last week" subtitle="Daily paid revenue comparison" wide>
              {weeklyComparison.length ? <div className="ra-chart-canvas"><ResponsiveContainer width="100%" height={chartHeight}>
                <LineChart data={weeklyComparison} margin={{ top: 8, right: 20, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" />
                  <YAxis width={65} tickFormatter={v => compactMoney(v, currency)} />
                  <Tooltip formatter={v => money(v, currency)} /><Legend />
                  <Line type="monotone" dataKey="current" name="This week" stroke="#2563eb" strokeWidth={3} />
                  <Line type="monotone" dataKey="previous" name="Last week" stroke="#10b981" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer></div> : <EmptyChart>Previous-week sales are not included in the current API response.</EmptyChart>}
            </ChartPanel>
          </div>
          <p className="ra-analytics-note">
            Revenue uses paid, non-cancelled orders and does not separately deduct refunds. Item rankings use saved order-item names.
            Category, low-selling item and previous-week charts use your existing restaurant analytics API.
          </p>
        </>
      )}
    </div>
  );
}